<script setup lang="ts">
/**
 * Locale switch.
 *
 * Switching locale changes the interface, not the URL — which means it does not change what search
 * engines index. The prerendered documents exist in one language, and a site that needs indexable
 * translations must serve them from distinct paths cross-linked with `hreflang`. See
 * `docs/guide/seo.md`.
 *
 * `document.documentElement.lang` is updated here rather than through `useSeo()` for the same
 * reason: the head describes the indexed document, this describes what the visitor is currently
 * reading. Screen readers switch pronunciation on the attribute, so it has to follow the visible
 * language.
 */

/**
 * Vue
 */
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { Languages } from '@lucide/vue'

const { t, locale, availableLocales } = useI18n()

watch(
  locale,
  value => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = value
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('locale', value)
    }
  },
  { immediate: true }
)

function cycle(): void {
  const index = availableLocales.indexOf(locale.value)
  locale.value = availableLocales[(index + 1) % availableLocales.length]
}
</script>

<template>
  <button
    type="button"
    class="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border bg-background/60 px-3 text-xs font-semibold uppercase text-foreground backdrop-blur transition hover:border-primary/40 hover:text-primary"
    :aria-label="t('actions.switchLanguage')"
    @click="cycle()"
  >
    <Languages class="h-4 w-4" aria-hidden="true" />
    {{ locale }}
  </button>
</template>
